import { CheckCircle2, AlertCircle, ShieldCheck, Wrench, TrendingUp, HelpCircle, XCircle, IndianRupee } from "lucide-react";
import { getServiceExtension } from "../../data/serviceExtensions";

/**
 * ServiceExtendedContent — Jul 2026 SEO expansion.
 *
 * Renders 6 rich sections below the existing landing-page hero + intro:
 *   1. How the procedure works (step-by-step)
 *   2. Comparison table (IOLs / injections / squint types)
 *   3. Recovery timeline
 *   4. Common misconceptions (FAQ-schema eligible)
 *   5. When NOT to have the surgery (E-E-A-T signal)
 *   6. Insurance & cost overview
 *
 * Data-driven: content is loaded from `/data/serviceExtensions.js` by
 * landing-page slug.  If no extension exists for a slug, the whole block
 * silently renders nothing — safe fallback for any future service page.
 */
const ServiceExtendedContent = ({ slug }) => {
  const ext = getServiceExtension(slug);
  if (!ext) return null;

  return (
    <section className="border-t border-border bg-background" data-testid={`svc-ext-${slug}`}>
      {/* Procedure steps */}
      {ext.steps?.length > 0 && (
        <div className="py-14 container-page" data-testid="svc-ext-steps">
          <div className="overline mb-3 inline-flex items-center gap-2">
            <Wrench size={14} strokeWidth={1.5} /> How it works, step by step
          </div>
          <h2 className="font-serif text-3xl sm:text-4xl">The procedure, in the order it happens</h2>
          <div className="h-[2px] w-12 bg-[hsl(var(--accent))] mt-3 mb-8" />
          <ol className="grid md:grid-cols-2 gap-4">
            {ext.steps.map((s, i) => (
              <li key={i} className="rounded-2xl border border-border p-5 bg-background flex gap-4">
                <div className="w-8 h-8 rounded-full bg-[hsl(var(--accent))]/12 text-[hsl(var(--accent))] flex items-center justify-center font-serif text-lg shrink-0">
                  {i + 1}
                </div>
                <div>
                  <div className="font-serif text-lg">{s.t}</div>
                  <p className="text-sm text-muted-foreground mt-1">{s.d}</p>
                </div>
              </li>
            ))}
          </ol>
        </div>
      )}

      {/* Comparison table */}
      {ext.tableRows?.length > 0 && (
        <div className="py-14 border-t border-border container-page" data-testid="svc-ext-table">
          <div className="overline mb-3 inline-flex items-center gap-2">
            <TrendingUp size={14} strokeWidth={1.5} /> Decision matrix
          </div>
          <h2 className="font-serif text-3xl sm:text-4xl">{ext.tableTitle}</h2>
          <div className="h-[2px] w-12 bg-[hsl(var(--accent))] mt-3 mb-8" />
          <div className="rounded-2xl border border-border overflow-x-auto bg-background">
            <table className="w-full text-sm">
              <thead className="bg-[hsl(var(--secondary))]/60 text-[10px] uppercase tracking-[0.18em]">
                <tr>{ext.tableHeaders.map((h) => <th key={h} className="text-left px-4 py-3 border-b border-border">{h}</th>)}</tr>
              </thead>
              <tbody>
                {ext.tableRows.map((row, i) => (
                  <tr key={i} className="border-b border-border last:border-b-0">
                    {row.map((c, j) => (
                      <td key={j} className={`px-4 py-3 align-top ${j === 0 ? "font-medium" : "text-muted-foreground"}`}>{c}</td>
                    ))}
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>
      )}

      {/* Recovery timeline */}
      {ext.recovery?.length > 0 && (
        <div className="py-14 border-t border-border container-page" data-testid="svc-ext-recovery">
          <div className="overline mb-3 inline-flex items-center gap-2">
            <CheckCircle2 size={14} strokeWidth={1.5} /> Recovery timeline
          </div>
          <h2 className="font-serif text-3xl sm:text-4xl">What happens Day 1 to Week 6</h2>
          <div className="h-[2px] w-12 bg-[hsl(var(--accent))] mt-3 mb-8" />
          <div className="space-y-3">
            {ext.recovery.map((r, i) => (
              <div key={i} className="rounded-xl border border-border p-4 flex gap-4 bg-background">
                <div className="w-32 shrink-0 text-[11px] uppercase tracking-[0.18em] text-[hsl(var(--accent))] pt-0.5">{r.t}</div>
                <p className="text-sm text-muted-foreground">{r.d}</p>
              </div>
            ))}
          </div>
        </div>
      )}

      {/* Misconceptions (FAQ-styled) */}
      {ext.misconceptions?.length > 0 && (
        <div className="py-14 border-t border-border container-page" data-testid="svc-ext-misconceptions">
          <div className="overline mb-3 inline-flex items-center gap-2">
            <HelpCircle size={14} strokeWidth={1.5} /> Common misconceptions
          </div>
          <h2 className="font-serif text-3xl sm:text-4xl">Things patients often get wrong</h2>
          <div className="h-[2px] w-12 bg-[hsl(var(--accent))] mt-3 mb-8" />
          <div className="space-y-4">
            {ext.misconceptions.map((m, i) => (
              <details key={i} className="group rounded-2xl border border-border p-5 bg-background">
                <summary className="cursor-pointer list-none flex items-start justify-between gap-4">
                  <div className="font-serif text-lg">{m.q}</div>
                  <span className="text-[hsl(var(--accent))] shrink-0 group-open:rotate-45 transition-transform text-xl leading-none">+</span>
                </summary>
                <p className="mt-3 text-sm text-muted-foreground leading-relaxed">{m.a}</p>
              </details>
            ))}
          </div>
        </div>
      )}

      {/* When NOT to have this surgery */}
      {ext.whenNot?.length > 0 && (
        <div className="py-14 border-t border-border container-page" data-testid="svc-ext-whennot">
          <div className="overline mb-3 inline-flex items-center gap-2">
            <XCircle size={14} strokeWidth={1.5} /> When NOT to have this surgery
          </div>
          <h2 className="font-serif text-3xl sm:text-4xl">Honest counsel — when to pause, treat elsewhere, or defer</h2>
          <div className="h-[2px] w-12 bg-[hsl(var(--accent))] mt-3 mb-8" />
          <ul className="space-y-2">
            {ext.whenNot.map((w, i) => (
              <li key={i} className="flex items-start gap-2 text-sm">
                <AlertCircle size={16} className="text-[hsl(var(--muted-foreground))] mt-0.5 shrink-0" />
                <span>{w}</span>
              </li>
            ))}
          </ul>
        </div>
      )}

      {/* Insurance + cost overview */}
      {ext.insurance && (
        <div className="py-14 border-t border-border bg-[hsl(var(--secondary))]/40" data-testid="svc-ext-insurance">
          <div className="container-page">
            <div className="overline mb-3 inline-flex items-center gap-2">
              <IndianRupee size={14} strokeWidth={1.5} /> Insurance &amp; cost
            </div>
            <h2 className="font-serif text-3xl sm:text-4xl">What's covered, what's not</h2>
            <div className="h-[2px] w-12 bg-[hsl(var(--accent))] mt-3 mb-6" />
            <p className="max-w-3xl text-sm text-muted-foreground leading-relaxed">{ext.insurance}</p>
          </div>
        </div>
      )}
    </section>
  );
};

export default ServiceExtendedContent;
